import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  type RefObject,
} from "react";

/**
 * How close to the end of the thread still counts as "at the bottom".
 *
 * Generous on purpose: a reader who is one line short of the end is reading
 * the latest message, and a threshold of a few pixels would drop them out of
 * following on sub-pixel rounding alone.
 */
export const CHAT_BOTTOM_THRESHOLD_PX = 32;

/** The attribute every thread row carries, so rows can be found and anchored. */
export const CHAT_ROW_ATTRIBUTE = "data-chat-row-id";

/** A persisted row's conversation sequence, for navigation across history. */
export const CHAT_ROW_SEQUENCE_ATTRIBUTE = "data-chat-row-sequence";

/** Set on a row for a moment after the reader was taken to it. */
export const CHAT_ROW_HIGHLIGHT_ATTRIBUTE = "data-chat-highlight";

/** How long a navigated-to row stays highlighted. */
export const CHAT_HIGHLIGHT_MS = 2000;

export interface ChatScrollRow {
  readonly id: string;
  readonly newMessageKey?: string | undefined;
}

export interface ChatScrollFollow {
  /** The reader is away from the bottom, so the jump control shows. */
  readonly awayFromBottom: boolean;
  /** New human or Player AI messages since the reader left the bottom. */
  readonly newCount: number;
  /** Scroll to the latest row, clear the count and resume following. */
  readonly jumpToLatest: () => void;
  /**
   * Bring one row to the middle of the thread, stop following there and
   * (optionally) highlight it briefly. Returns false when the row is not
   * rendered, so the host can load it.
   */
  readonly revealRow: (
    rowId: string,
    options?: { readonly highlight?: boolean },
  ) => boolean;
  /** Sequences of the first and last persisted rows currently on screen. */
  readonly visibleSequenceRange: () => {
    first: number | null;
    last: number | null;
  };
  /** Wire to the thread's `onScroll`. */
  readonly onScroll: () => void;
}

interface Anchor {
  readonly id: string;
  /** The row's top edge relative to the thread's top edge, in px. */
  readonly top: number;
}

/**
 * Where the reader left the live end: the newest countable row they had seen.
 * Everything countable after it is new. Keyed by conversation, so a marker can
 * never describe a thread other than the one on screen.
 */
interface ReadingMarker {
  readonly resetKey: string | null | undefined;
  readonly afterKey: string | null;
}

/**
 * Keep the reader where they are (Task 1 / Task 11).
 *
 * Two states, and nothing else moves the thread:
 *
 * - FOLLOWING (at the bottom): every DOM change — a new message, a streamed
 *   chunk, the thinking row, a card growing — ends with the thread scrolled to
 *   its end.
 * - READING (scrolled up): every DOM change keeps the first visible row at the
 *   same offset. That covers content arriving below (a no-op), older history
 *   prepended above, and a Player AI update or a proposal collapsing ABOVE the
 *   reader. The browser's own scroll anchoring does not exist in every engine,
 *   and would double the correction where it does, so the thread turns it off
 *   (`overflow-anchor: none`) and this is the one mechanism.
 *
 * Only the reader's own upward scroll leaves FOLLOWING, so content that grows
 * without re-rendering this component cannot silently drop them out of it.
 * The new-message count is DERIVED from the rows and a marker, never
 * accumulated: older history prepended above the marker is not "new", and a
 * streamed draft and its final message share one key, so a reply counts once.
 */
export function useChatScrollFollow({
  scrollRef,
  contentRef,
  rows,
  resetKey,
}: {
  scrollRef: RefObject<HTMLDivElement | null>;
  /** The element wrapping every row (its size is observed). */
  contentRef: RefObject<HTMLDivElement | null>;
  rows: readonly ChatScrollRow[];
  /** Changing this (a different conversation) starts again at the bottom. */
  resetKey: string | null | undefined;
}): ChatScrollFollow {
  const followingRef = useRef(true);
  const anchorRef = useRef<Anchor | null>(null);
  const lastScrollTopRef = useRef(0);
  const resetKeyRef = useRef<string | null | undefined>(resetKey);
  const rowsRef = useRef<readonly ChatScrollRow[]>(rows);
  const [marker, setMarker] = useState<ReadingMarker | null>(null);

  const captureAnchor = useCallback((): void => {
    const thread = scrollRef.current;
    if (!thread) return;
    const threadTop = thread.getBoundingClientRect().top;
    anchorRef.current = null;
    for (const row of threadRows(thread)) {
      const rect = row.getBoundingClientRect();
      if (rect.bottom > threadTop + 1) {
        const id = row.getAttribute(CHAT_ROW_ATTRIBUTE);
        if (id !== null) anchorRef.current = { id, top: rect.top - threadTop };
        return;
      }
    }
  }, [scrollRef]);

  const reconcile = useCallback((): void => {
    const thread = scrollRef.current;
    if (!thread) return;
    if (followingRef.current) {
      // Clamped to the real maximum by the browser; a no-op (and no scroll
      // event) when the thread is already at its end.
      thread.scrollTop = thread.scrollHeight;
    } else {
      const anchor = anchorRef.current;
      const row = anchor ? findRow(thread, anchor.id) : null;
      if (anchor && row) {
        const delta =
          row.getBoundingClientRect().top -
          thread.getBoundingClientRect().top -
          anchor.top;
        if (Math.abs(delta) >= 1) thread.scrollTop += delta;
      }
    }
    lastScrollTopRef.current = thread.scrollTop;
    captureAnchor();
  }, [captureAnchor, scrollRef]);

  // Reconcile row changes; ResizeObserver handles other geometry changes.
  // Draft-only renders must not scan every message or force layout.
  // A different conversation starts again at its live end.
  useLayoutEffect(() => {
    rowsRef.current = rows;
    if (resetKeyRef.current !== resetKey) {
      resetKeyRef.current = resetKey;
      followingRef.current = true;
      anchorRef.current = null;
    }
    reconcile();
  }, [rows, resetKey, reconcile]);

  // Content that resizes without re-rendering this component — an image, a
  // card's own disclosure, a font swap — settles the same way.
  useEffect(() => {
    const content = contentRef.current;
    if (!content || typeof ResizeObserver === "undefined") return;
    const observer = new ResizeObserver(() => reconcile());
    observer.observe(content);
    return () => observer.disconnect();
  }, [contentRef, reconcile]);

  const markReading = useCallback((): void => {
    const currentKey = resetKeyRef.current;
    setMarker((previous) =>
      previous !== null && previous.resetKey === currentKey
        ? previous
        : { resetKey: currentKey, afterKey: newestKey(rowsRef.current) },
    );
  }, []);

  const onScroll = useCallback((): void => {
    const thread = scrollRef.current;
    if (!thread) return;
    const distance =
      thread.scrollHeight - thread.scrollTop - thread.clientHeight;
    if (distance <= CHAT_BOTTOM_THRESHOLD_PX) {
      followingRef.current = true;
      setMarker(null);
    } else if (thread.scrollTop < lastScrollTopRef.current - 1) {
      // Only the reader's own upward movement leaves the live end.
      followingRef.current = false;
      markReading();
    }
    lastScrollTopRef.current = thread.scrollTop;
    captureAnchor();
  }, [captureAnchor, markReading, scrollRef]);

  const jumpToLatest = useCallback((): void => {
    followingRef.current = true;
    setMarker(null);
    const thread = scrollRef.current;
    if (!thread) return;
    thread.scrollTop = thread.scrollHeight;
    lastScrollTopRef.current = thread.scrollTop;
    captureAnchor();
  }, [captureAnchor, scrollRef]);

  const highlightTimersRef = useRef<Map<HTMLElement, number>>(new Map());
  useEffect(() => {
    const timers = highlightTimersRef.current;
    return () => {
      for (const timer of timers.values()) window.clearTimeout(timer);
      timers.clear();
    };
  }, []);

  const highlight = useCallback((row: HTMLElement): void => {
    // Imperative on purpose: a transient visual cue is not render state, and
    // the attribute is not one React manages, so a re-render never drops it.
    const timers = highlightTimersRef.current;
    window.clearTimeout(timers.get(row));
    row.setAttribute(CHAT_ROW_HIGHLIGHT_ATTRIBUTE, "true");
    timers.set(
      row,
      window.setTimeout(() => {
        row.removeAttribute(CHAT_ROW_HIGHLIGHT_ATTRIBUTE);
        timers.delete(row);
      }, CHAT_HIGHLIGHT_MS),
    );
  }, []);

  const visibleSequenceRange = useCallback((): {
    first: number | null;
    last: number | null;
  } => {
    const thread = scrollRef.current;
    if (!thread) return { first: null, last: null };
    const threadRect = thread.getBoundingClientRect();
    let first: number | null = null;
    let last: number | null = null;
    for (const row of threadRows(thread)) {
      const raw = row.getAttribute(CHAT_ROW_SEQUENCE_ATTRIBUTE);
      if (raw === null) continue;
      const sequence = Number(raw);
      if (!Number.isFinite(sequence)) continue;
      const rect = row.getBoundingClientRect();
      if (rect.bottom <= threadRect.top || rect.top >= threadRect.bottom) {
        continue;
      }
      first = first === null ? sequence : Math.min(first, sequence);
      last = last === null ? sequence : Math.max(last, sequence);
    }
    return { first, last };
  }, [scrollRef]);

  const revealRow = useCallback(
    (rowId: string, options?: { readonly highlight?: boolean }): boolean => {
      const thread = scrollRef.current;
      const row = thread ? findRow(thread, rowId) : null;
      if (!thread || !row) return false;
      if (options?.highlight) highlight(row);
      const threadRect = thread.getBoundingClientRect();
      const rowRect = row.getBoundingClientRect();
      // One axis, one offset: never scrollIntoView on a single-axis scroller.
      const offset =
        rowRect.top -
        threadRect.top -
        Math.max(0, (thread.clientHeight - rowRect.height) / 2);
      thread.scrollTop = Math.max(0, thread.scrollTop + offset);
      lastScrollTopRef.current = thread.scrollTop;
      const distance =
        thread.scrollHeight - thread.scrollTop - thread.clientHeight;
      if (distance <= CHAT_BOTTOM_THRESHOLD_PX) {
        followingRef.current = true;
        setMarker(null);
      } else {
        followingRef.current = false;
        markReading();
      }
      captureAnchor();
      return true;
    },
    [captureAnchor, highlight, markReading, scrollRef],
  );

  const current =
    marker !== null && marker.resetKey === resetKey ? marker : null;
  return {
    awayFromBottom: current !== null,
    newCount: current === null ? 0 : countAfter(rows, current.afterKey),
    jumpToLatest,
    revealRow,
    visibleSequenceRange,
    onScroll,
  };
}

function countableKeys(rows: readonly ChatScrollRow[]): string[] {
  return rows.flatMap((row) =>
    typeof row.newMessageKey === "string" && row.newMessageKey.length > 0
      ? [row.newMessageKey]
      : [],
  );
}

function newestKey(rows: readonly ChatScrollRow[]): string | null {
  return countableKeys(rows).at(-1) ?? null;
}

/**
 * Distinct countable keys after `afterKey`. A marker whose row has since left
 * the list counts nothing rather than everything: a vanished marker is not
 * evidence that the whole transcript is new.
 */
export function countAfter(
  rows: readonly ChatScrollRow[],
  afterKey: string | null,
): number {
  const keys = countableKeys(rows);
  const start = afterKey === null ? 0 : keys.lastIndexOf(afterKey) + 1;
  if (afterKey !== null && start === 0) return 0;
  return new Set(keys.slice(start)).size;
}

function threadRows(thread: HTMLElement): HTMLElement[] {
  return Array.from(
    thread.querySelectorAll<HTMLElement>(`[${CHAT_ROW_ATTRIBUTE}]`),
  );
}

function findRow(thread: HTMLElement, rowId: string): HTMLElement | null {
  return (
    threadRows(thread).find(
      (row) => row.getAttribute(CHAT_ROW_ATTRIBUTE) === rowId,
    ) ?? null
  );
}
